import { apiClient } from '../lib/apiClient';
import type { ParameterTarget, TargetSource } from './measurementService';

/**
 * Parameter targets of an aquarium (Reef Guard #1819): the hobby default or the
 * user's personal set point ± tolerance per parameter. The backend resolves
 * them (src/services/parameterTargetService.ts); the frontend never keeps its
 * own range table.
 */
export interface ResolvedParameterTarget {
  readonly parameterId: string;
  readonly min: number;
  readonly max: number;
  readonly margin: number;
  readonly unit: string;
  readonly source: TargetSource;
  readonly setPoint: number | null;
  readonly tolerance: number | null;
  readonly defaultMin: number;
  readonly defaultMax: number;
}

export interface SetPointInput {
  readonly set_point: number;
  readonly tolerance: number;
}

const base = (aquariumId: string): string => `/api/v1/aquariums/${aquariumId}/parameter-targets`;

/** GET …/parameter-targets — every parameter's target and its source. */
export async function listParameterTargets(aquariumId: string): Promise<ResolvedParameterTarget[]> {
  const response = await apiClient.get<{ data: ResolvedParameterTarget[] }>(base(aquariumId));
  return response.data;
}

/** PUT …/parameter-targets/:parameterId/set-point — create or replace a personal set point. */
export async function saveSetPoint(aquariumId: string, parameterId: string, input: SetPointInput): Promise<ResolvedParameterTarget> {
  const response = await apiClient.put<{ data: ResolvedParameterTarget }>(
    `${base(aquariumId)}/${encodeURIComponent(parameterId)}/set-point`,
    input,
  );
  return response.data;
}

/** DELETE …/parameter-targets/:parameterId/set-point — back to the default. */
export async function clearSetPoint(aquariumId: string, parameterId: string): Promise<ResolvedParameterTarget> {
  const response = await apiClient.delete<{ data: ResolvedParameterTarget }>(
    `${base(aquariumId)}/${encodeURIComponent(parameterId)}/set-point`,
  );
  return response.data;
}

/** Short keys / ICP symbols of the canonical parameter ids (backend canonicalParameterId). */
const SHORT_KEYS: Readonly<Record<string, readonly string[]>> = {
  alkalinity: ['kh', 'alk'],
  calcium: ['ca'],
  magnesium: ['mg'],
  nitrate: ['no3'],
  phosphate: ['po4'],
  iodine: ['i'],
  potassium: ['k'],
  strontium: ['sr'],
  temperature: ['temp'],
};

/**
 * Targets keyed by lower-case parameter id and its short keys ("kh", "sr", …),
 * so a stored id, an ICP symbol or a display name all find their target.
 */
export function targetsById(targets: readonly ResolvedParameterTarget[]): ReadonlyMap<string, ParameterTarget> {
  const map = new Map<string, ParameterTarget>();
  for (const target of targets) {
    map.set(target.parameterId, target);
    for (const key of SHORT_KEYS[target.parameterId] ?? []) map.set(key, target);
  }
  return map;
}
